<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { NOTIFICATION_GROUPINGS } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import NotificationMark from '@/components/notifications/notification-mark.vue';
import { NavigationMenu } from '@/types';
import { useInsightStatusBadge } from '@/utils/composables/useInsightStatusBadge';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import {
  useActiveUserDocumentContract,
  useGroups,
  useNotificationsUnviewedUnreadCount,
  useUserDocumentUsage,
  useUserInfo,
} from '@/utils/swr';
import { useUserDocumentLimitNotification } from '@/utils/user-document/userDocumentLimitNotification';
import { useStore } from '@/utils/vue';

interface Props {
  activeMenu?: NavigationMenu;
}

withDefaults(defineProps<Props>(), {
  activeMenu: '',
});

const { data: userInfo } = useUserInfo();
const { data: groups } = useGroups();
const store = useStore();
const isNextLayout = useIsNextLayout();
const isAdminUser = computed(() => userInfo.value?.role === 'admin');
const { data: userDocumentUsage } = useUserDocumentUsage(isAdminUser.value);
const { data: activeUserDocumentContract } =
  useActiveUserDocumentContract(isAdminUser);

const allUserGroupId = computed(
  () =>
    groups.value?.groups.find(group => group.group_type === 'all_user_group')
      ?.id,
);

const route = useRoute();
const { mutate: mutateUnviewedUnreadCount } =
  useNotificationsUnviewedUnreadCount(NOTIFICATION_GROUPINGS);
const updateNotifications = async () => {
  mutateUnviewedUnreadCount();
};
/** ルーティングが切り替わる都度通知の再フェッチ */
watch(() => route.path, updateNotifications);

const { hasUnviewedInsight } = useInsightStatusBadge({ observe: true });

const { refreshNotification: refreshUserDocumentNotification } =
  useUserDocumentLimitNotification();
watch([isAdminUser, userDocumentUsage, activeUserDocumentContract], () => {
  refreshUserDocumentNotification(
    isAdminUser.value,
    userDocumentUsage.value,
    activeUserDocumentContract.value,
  );
});
const hasAdminNotification = computed(
  () => store.state.userDocumentNotification.hasNotification,
);
onMounted(() => {
  refreshUserDocumentNotification(
    isAdminUser.value,
    userDocumentUsage.value,
    activeUserDocumentContract.value,
  );
});
</script>

<template>
  <div class="o-section-container" :class="{ 'is-next-layout': isNextLayout }">
    <ul class="o-section-items">
      <li>
        <router-link
          :to="{ name: 'anewsHome' }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'news' }"
          target="_self"
        >
          <DgrIcon name="home" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">ホーム</div>
          <NotificationMark
            v-if="hasUnviewedInsight && activeMenu !== 'news'"
            top="8px"
            right="16px"
          />
        </router-link>
      </li>
      <li>
        <router-link
          :to="{ name: 'mypageProfile' }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'my_page' }"
          target="_self"
        >
          <DgrIcon
            name="user-circle"
            :keep-fill="false"
            class="o-section-icon"
          />
          <div class="o-section-text">マイページ</div>
        </router-link>
      </li>
      <li>
        <router-link
          :to="{
            name: 'groupActivity',
            params: { groupId: String(allUserGroupId) },
          }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'group' }"
          target="_self"
        >
          <DgrIcon name="users" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">グループ</div>
        </router-link>
      </li>
      <li>
        <router-link
          :to="{
            name: 'surveyTop',
          }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'survey' }"
          target="_self"
        >
          <DgrIcon
            name="sparkles-fill"
            :keep-fill="false"
            class="o-section-icon"
          />
          <div class="o-section-text">要約</div>
        </router-link>
      </li>
      <li>
        <router-link
          :to="{ name: 'others' }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'others' }"
          target="_self"
        >
          <DgrIcon name="bars" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">その他</div>
        </router-link>
      </li>
      <li v-if="isAdminUser">
        <router-link
          :to="{
            name: 'contracts',
          }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'admin' }"
          target="_self"
        >
          <DgrIcon name="cog" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">管理・設定</div>
          <NotificationMark v-if="hasAdminNotification" right="8px" top="8px" />
        </router-link>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.o-section-container {
  position: fixed;
  top: $header-height;
  left: 0;
  display: flex;
  justify-content: center;
  width: $left-section-width;
  height: calc(100vh - #{$header-height}); // global headerの高さを引く
  padding: 20px 8px;
  box-sizing: border-box;
  border-right: 1px solid $color-gray400;
  justify-content: space-between;
  flex-direction: column;

  &.is-next-layout {
    position: static;
    width: 100%;
    height: 100%;
    border-right: none;
  }
}

ul.o-section-items {
  list-style: none;
  :hover.o-section-item {
    background-color: $color-gray200;
  }
  :hover.o-section-item.active {
    background: $color-green100;
  }
}

.o-section {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 58px;
  border-radius: 4px;
  margin-bottom: 8px;
  &.active {
    background: $color-green100;
  }
}

.o-section-text {
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 10px;
  text-align: center;
  margin-top: 6px;
}

/** 親要素がactiveのclassを持つ場合子要素をあわせてスタイルをハイライトする */
.o-section-item.active > .o-section-icon {
  color: $color-green600;
}
.o-section-item.active > .o-section-text {
  color: $color-green600;
}
</style>
